import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Button, TextField, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Autocomplete,
  alpha, useTheme, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, List, ListItem, ListItemButton, ListItemText, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Refresh as RefreshIcon, Print as PrintIcon, FilterList as FilterIcon,
  AccountBalance as CustodyIcon, ShoppingCart as PurchaseIcon,
  Storefront as BranchIcon, ReceiptLong as InvoiceIcon, Person as PersonIcon,
  Security as SecurityIcon, Save as SaveIcon,
} from '@mui/icons-material';
import custodyPurchasesReportService, {
  CustodyPurchasesReport, MyCustodyOption, CentralCustody, AssignableUser
} from '../services/custodyPurchasesReport.service';
import { toast } from 'react-toastify';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';

const CustodyPurchasesReportPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { minDate, maxDate } = useFiscalDateRange();
  const today = new Date();
  // الافتراضي: بداية الشهر الحالي إلى اليوم، لكن ضمن نطاق السنة المالية
  const defaultFrom = new Date(Math.max(new Date(today.getFullYear(), today.getMonth(), 1).getTime(), minDate.getTime()));
  const defaultTo = new Date(Math.min(today.getTime(), maxDate.getTime()));
  const toLocalISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [fromDate, setFromDate] = useState<Date | null>(defaultFrom);
  const [toDate, setToDate] = useState<Date | null>(defaultTo);
  const [custodies, setCustodies] = useState<MyCustodyOption[]>([]);
  const [selectedCustody, setSelectedCustody] = useState<MyCustodyOption | null>(null);
  const [viewAll, setViewAll] = useState(false);
  const [report, setReport] = useState<CustodyPurchasesReport | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Access management dialog (for VIEW_ALL users) ──
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [centralCustodies, setCentralCustodies] = useState<CentralCustody[]>([]);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<AssignableUser | null>(null);
  const [selectedAccessCustodyIds, setSelectedAccessCustodyIds] = useState<Set<number>>(new Set());
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);

  const t = {
    title: isRTL ? 'تقرير مشتريات العُهد' : 'Custody Purchases Report',
    subtitle: isRTL ? 'تفاصيل مشتريات كل عهدة بحسب الفرع' : 'Purchase details per custody by branch',
    from: isRTL ? 'من' : 'From',
    to: isRTL ? 'إلى' : 'To',
    custody: isRTL ? 'العهدة' : 'Custody',
    allCustodies: isRTL ? 'كل العُهد' : 'All Custodies',
    runReport: isRTL ? 'تشغيل التقرير' : 'Run Report',
    print: isRTL ? 'طباعة' : 'Print',
    running: isRTL ? 'جارٍ...' : 'Loading...',
    period: isRTL ? 'الفترة' : 'Period',
    generated: isRTL ? 'تم الإنشاء' : 'Generated',
    holderName: isRTL ? 'حامل العهدة' : 'Custody Holder',
    currentBalance: isRTL ? 'الرصيد الحالي' : 'Current Balance',
    initialAmount: isRTL ? 'الرصيد الافتتاحي' : 'Initial Amount',
    custodyType: isRTL ? 'نوع العهدة' : 'Type',
    // Stats
    totalCustodies: isRTL ? 'العُهد' : 'Custodies',
    totalRequests: isRTL ? 'الطلبات' : 'Requests',
    totalPOs: isRTL ? 'أوامر الشراء' : 'Purchase Orders',
    totalInvoices: isRTL ? 'الفواتير' : 'Invoices',
    totalAmount: isRTL ? 'إجمالي المبلغ' : 'Total Amount',
    branchesCovered: isRTL ? 'الفروع المخدومة' : 'Branches Covered',
    // Table
    branch: isRTL ? 'الفرع' : 'Branch',
    requestCount: isRTL ? 'طلبات' : 'Requests',
    poCount: isRTL ? 'أوامر شراء' : 'POs',
    invoiceCount: isRTL ? 'فواتير' : 'Invoices',
    amount: isRTL ? 'المبلغ (KWD)' : 'Amount (KWD)',
    totalRow: isRTL ? 'الإجمالي' : 'TOTAL',
    noData: isRTL ? 'لا توجد بيانات للفترة المحددة' : 'No data for the selected period',
    kwd: isRTL ? 'د.ك' : 'KWD',
    // Access dialog
    manageAccess: isRTL ? 'إدارة صلاحيات أمناء المخازن' : 'Manage Warehouse Keeper Access',
    accessDialogTitle: isRTL ? 'تحديد العُهد المرئية لأمناء المخازن' : 'Configure Custody Visibility for Warehouse Keepers',
    selectUser: isRTL ? 'اختر المستخدم' : 'Select User',
    accessHint: isRTL ? 'اختر المستخدم ثم العُهد المركزية التي يستطيع رؤية تقاريرها (تظهر فقط العُهد الرئيسية، ليست عهد الفروع)' : 'Select the user and the central custodies they can view (only main custodies shown, not branch ones)',
    save: isRTL ? 'حفظ' : 'Save',
    close: isRTL ? 'إغلاق' : 'Close',
    saved: isRTL ? 'تم الحفظ' : 'Saved',
    noCentral: isRTL ? 'لا توجد عُهد مركزية' : 'No central custodies available',
  };

  useEffect(() => {
    (async () => {
      // ✅ تحميل كل شيء بشكل مستقل ليصمد إن فشل أحدها
      try {
        const my = await custodyPurchasesReportService.myCustodies();
        const list = my?.custodies || [];
        setCustodies(list);
        setViewAll(!!my?.viewAll);
        if (!my?.viewAll && list.length === 1) {
          setSelectedCustody(list[0]);
        }
      } catch (e: any) {
        const msg = e.response?.data?.message || e.message || 'Failed to load custodies';
        if (e.response?.status === 403) {
          setError(isRTL ? 'ليس لديك صلاحية لعرض هذا التقرير. تواصل مع السوبر أدمن أو سجّل خروج وادخل مجدداً.' : 'You do not have permission to view this report. Contact Super Admin or logout/login again.');
        } else {
          setError(msg);
        }
      }
      try {
        const ci = await companyInfoService.getCompanyInfo();
        setCompanyInfo(ci);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReport = async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    try {
      const data = await custodyPurchasesReportService.run(toLocalISO(fromDate), toLocalISO(toDate), selectedCustody?.custodyId);
      setReport(data);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ✅ نُشغّل التقرير بعد تحميل قائمة العُهد، فقط إذا كان لديه وصول
    if ((custodies?.length ?? 0) > 0 || viewAll) runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custodies?.length, viewAll]);

  const fmt = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  const openAccessDialog = async () => {
    setAccessDialogOpen(true);
    setAccessLoading(true);
    try {
      const [users, centrals] = await Promise.all([
        custodyPurchasesReportService.assignableUsers(),
        custodyPurchasesReportService.centralCustodies(),
      ]);
      setAssignableUsers(users);
      setCentralCustodies(centrals);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setAccessLoading(false);
    }
  };

  const onSelectUserForAccess = async (user: AssignableUser | null) => {
    setSelectedUserForAccess(user);
    setSelectedAccessCustodyIds(new Set());
    if (!user) return;
    try {
      const ids = await custodyPurchasesReportService.getKeeperAccess(user.userId);
      setSelectedAccessCustodyIds(new Set(ids));
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const toggleAccessCustody = (cid: number) => {
    setSelectedAccessCustodyIds(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const saveAccess = async () => {
    if (!selectedUserForAccess) return;
    setAccessSaving(true);
    try {
      await custodyPurchasesReportService.saveKeeperAccess(selectedUserForAccess.userId, Array.from(selectedAccessCustodyIds));
      toast.success(t.saved);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setAccessSaving(false);
    }
  };

  const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) => (
    <Paper sx={{
      p: 2, borderTop: `4px solid ${color}`,
      bgcolor: alpha(color, isDark ? 0.12 : 0.06),
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <Box sx={{ color, fontSize: 36, display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>{label}</Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 'bold' }}>{value}</Typography>
      </Box>
    </Paper>
  );

  // ═══════════ Print Function ═══════════
  const handlePrint = () => {
    if (!report) return;
    const pw = window.open('', '_blank', 'width=1100,height=800');
    if (!pw) return;

    const periodStr = `${new Date(report.periodFrom).toLocaleDateString('en-GB')} → ${new Date(report.periodTo).toLocaleDateString('en-GB')}`;
    const docTitle = isRTL ? 'تقرير مشتريات العُهد' : 'Custody Purchases Report';

    const headerHtml = `
      ${companyInfo ? `
      <div class="header">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
        <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <div class="subtitle">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? '📞 ' + companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | 🌐 ' + companyInfo.website : ''}
        </div>
      </div>
      ` : ''}
      <table class="title-bar"><tr>
        <td style="text-align:${isRTL ? 'right' : 'left'};"><h3>📊 ${docTitle}</h3></td>
        <td style="text-align:${isRTL ? 'left' : 'right'};"><div class="meta"><strong>${t.period}: ${periodStr}</strong></div></td>
      </tr></table>
      <table class="stats-tbl"><tr>
        <td><div class="stat-card s-emp"><div class="label">${t.totalCustodies}</div><div class="val">${report.totalCustodies}</div></div></td>
        <td><div class="stat-card s-bank"><div class="label">${t.totalRequests}</div><div class="val">${report.totalRequests}</div></div></td>
        <td><div class="stat-card s-net"><div class="label">${t.totalPOs}</div><div class="val">${report.totalPurchaseOrders}</div></div></td>
        <td><div class="stat-card s-earn"><div class="label">${t.totalInvoices}</div><div class="val">${report.totalInvoices}</div></div></td>
        <td><div class="stat-card s-ded"><div class="label">${t.totalAmount} (KWD)</div><div class="val">${fmt(report.totalAmount)}</div></div></td>
      </tr></table>
    `;

    const custodySections = report.custodies.map((c, idx) => {
      const branchRows = c.branches.map(b => `
        <tr>
          <td class="cell-name">${isRTL ? (b.branchNameAr || b.branchName) : b.branchName}</td>
          <td class="cell-num">${b.requestCount}</td>
          <td class="cell-num">${b.purchaseOrderCount}</td>
          <td class="cell-num">${b.invoiceCount}</td>
          <td class="cell-amt">${fmt(b.totalAmount)}</td>
        </tr>
      `).join('');
      return `
        ${idx > 0 ? '<div style="page-break-before: auto; margin-top: 24px;"></div>' : '<div style="margin-top: 14px;"></div>'}
        <div class="custody-card">
          <div class="custody-head">
            <div>
              <h4>${c.custodyNumber} — ${isRTL ? (c.holderNameAr || c.holderName) : c.holderName || '—'}</h4>
              <div class="custody-sub">${t.holderName}: ${c.holderEmployeeCode || '—'} &nbsp;|&nbsp; ${t.custodyType}: ${c.custodyType || '—'} &nbsp;|&nbsp; ${t.currentBalance}: ${fmt(c.currentBalance)} ${t.kwd} &nbsp;|&nbsp; ${t.initialAmount}: ${fmt(c.initialAmount)} ${t.kwd}</div>
            </div>
            <div class="custody-totals">
              <div><b>${c.totalRequests}</b> ${t.totalRequests}</div>
              <div><b>${c.totalPurchaseOrders}</b> ${t.totalPOs}</div>
              <div><b>${c.totalInvoices}</b> ${t.totalInvoices}</div>
              <div><b>${fmt(c.totalAmount)}</b> ${t.kwd}</div>
            </div>
          </div>
          <table class="main">
            <thead>
              <tr>
                <th style="text-align:${isRTL ? 'right' : 'left'};">${t.branch}</th>
                <th>${t.requestCount}</th>
                <th>${t.poCount}</th>
                <th>${t.invoiceCount}</th>
                <th style="text-align:${isRTL ? 'left' : 'right'};">${t.amount}</th>
              </tr>
            </thead>
            <tbody>
              ${branchRows || `<tr><td colspan="5" style="text-align:center;color:#999;padding:14px;">${t.noData}</td></tr>`}
            </tbody>
            <tfoot>
              <tr>
                <td style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;">${t.totalRow}</td>
                <td>${c.totalRequests}</td>
                <td>${c.totalPurchaseOrders}</td>
                <td>${c.totalInvoices}</td>
                <td class="cell-amt">${fmt(c.totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      `;
    }).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}">
<head><meta charset="UTF-8"><title>${docTitle}</title><style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 18px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #1a1a1a; background: #fff; }
  .header { text-align: center; border-bottom: 3px solid #00796B; padding-bottom: 14px; margin-bottom: 14px; }
  .header img { max-height: 60px; margin-bottom: 6px; }
  .header h2 { color: #00796B; margin: 4px 0; font-size: 20px; }
  .header .subtitle { font-size: 11px; color: #555; }
  table.title-bar { width: 100%; border-collapse: collapse; background: linear-gradient(135deg, #00796B 0%, #00ACC1 100%); color: #fff; border-radius: 8px; margin-bottom: 14px; overflow: hidden; }
  table.title-bar td { padding: 12px 20px; vertical-align: middle; }
  table.title-bar h3 { font-size: 18px; margin: 0; }
  table.title-bar .meta { font-size: 12px; opacity: 0.95; }
  table.stats-tbl { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin-bottom: 8px; }
  table.stats-tbl td { width: 20%; padding: 0; vertical-align: top; }
  .stat-card { padding: 10px 12px; border-radius: 8px; color: #fff; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
  .stat-card .label { font-size: 10px; opacity: 0.95; margin-bottom: 4px; }
  .stat-card .val { font-size: 16px; font-weight: bold; }
  .s-emp { background: linear-gradient(135deg, #6A1B9A, #AB47BC); }
  .s-earn { background: linear-gradient(135deg, #00796B, #26A69A); }
  .s-ded { background: linear-gradient(135deg, #C62828, #EF5350); }
  .s-net { background: linear-gradient(135deg, #1565C0, #42A5F5); }
  .s-bank { background: linear-gradient(135deg, #EF6C00, #FFA726); }
  .custody-card { border: 2px solid #00796B; border-radius: 8px; padding: 12px; margin-bottom: 16px; page-break-inside: avoid; }
  .custody-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1.5px solid #00796B; }
  .custody-head h4 { color: #00796B; font-size: 15px; margin: 0; }
  .custody-sub { font-size: 11px; color: #555; margin-top: 4px; }
  .custody-totals { font-size: 11px; text-align: ${isRTL ? 'left' : 'right'}; color: #555; }
  .custody-totals div { margin-bottom: 2px; }
  .custody-totals b { color: #00796B; font-size: 13px; }
  table.main { width: 100%; border-collapse: collapse; font-size: 11px; }
  table.main thead th { background: #00796B; color: #fff; padding: 8px 10px; text-align: center; font-weight: 600; border: 1px solid #00564E; font-size: 11px; }
  table.main tbody td { padding: 7px 10px; border: 1px solid #DDD; text-align: center; }
  table.main tbody tr:nth-child(odd) td { background: #FAFAFA; }
  .cell-name { text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 12px !important; font-weight: 600; }
  .cell-num { color: #5E35B1; font-weight: 600; }
  .cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; padding-${isRTL ? 'left' : 'right'}: 12px !important; font-variant-numeric: tabular-nums; color: #00796B; font-weight: bold; }
  tfoot tr td { background: linear-gradient(135deg, #00796B, #00ACC1) !important; color: #fff !important; font-weight: bold; padding: 9px 10px !important; font-size: 12px !important; border: 1px solid #00564E !important; }
  tfoot tr td.cell-amt { color: #fff !important; }
  .footer { margin-top: 28px; display: flex; justify-content: space-between; gap: 30px; }
  .sig { text-align: center; flex: 1; }
  .sig .line { border-top: 1.5px solid #333; margin-top: 50px; padding-top: 6px; font-weight: bold; font-size: 11px; color: #333; }
  .footer-meta { margin-top: 14px; text-align: center; font-size: 9px; color: #999; }
  @media print {
    @page { size: A4; margin: 10mm; }
    body { padding: 0; }
  }
</style></head>
<body>
${headerHtml}
${custodySections}
<div class="footer">
  <div class="sig"><div class="line">${isRTL ? 'حامل العهدة' : 'Custody Holder'}</div></div>
  <div class="sig"><div class="line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
  <div class="sig"><div class="line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
</div>
<div class="footer-meta">${isRTL ? 'تم الإنشاء في' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
</body></html>`;
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    setTimeout(() => pw.print(), 600);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #00796B 0%, #00ACC1 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CustodyIcon sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{t.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.subtitle}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.from}
            value={fromDate}
            onChange={setFromDate}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } }}
          />
          <DatePicker
            label={t.to}
            value={toDate}
            onChange={setToDate}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 170 } } }}
          />
        </LocalizationProvider>

        {(viewAll || (custodies?.length ?? 0) > 0) && (
          <Autocomplete
            sx={{ minWidth: 360 }}
            size="small"
            options={custodies}
            value={selectedCustody}
            onChange={(_, v) => setSelectedCustody(v)}
            getOptionLabel={(o) => `${o.custodyNumber} — ${isRTL ? (o.employeeNameAr || o.employeeName) : o.employeeName || '—'}`}
            isOptionEqualToValue={(a, b) => a.custodyId === b.custodyId}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.custodyId} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#00796B' }}>
                  {option.custodyNumber}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {isRTL ? (option.employeeNameAr || option.employeeName) : (option.employeeName || option.employeeNameAr)} — {t.currentBalance}: {fmt(option.currentBalance)} {t.kwd}
                </Typography>
              </Box>
            )}
            renderInput={(p) => (
              <TextField
                {...p}
                label={viewAll ? `${t.custody} (${t.allCustodies})` : t.custody}
                placeholder={isRTL ? 'اكتب لتبحث...' : 'Type to search...'}
              />
            )}
          />
        )}

        <Button variant="contained" startIcon={<RefreshIcon />} onClick={runReport} disabled={loading} sx={{ bgcolor: '#00796B' }}>
          {loading ? t.running : t.runReport}
        </Button>
        {report && (
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ color: '#00796B', borderColor: '#00796B' }}>
            {t.print}
          </Button>
        )}
        {viewAll && (
          <Button variant="outlined" startIcon={<SecurityIcon />} onClick={openAccessDialog} sx={{ color: '#6A1B9A', borderColor: '#6A1B9A' }}>
            {t.manageAccess}
          </Button>
        )}
        {report && (
          <Typography variant="caption" sx={{ ml: 'auto', color: theme.palette.text.secondary }}>
            {t.generated}: {new Date(report.generatedAt).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !report && <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>}

      {report && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
            <StatCard icon={<CustodyIcon />} label={t.totalCustodies} value={report.totalCustodies} color="#6A1B9A" />
            <StatCard icon={<FilterIcon />} label={t.totalRequests} value={report.totalRequests} color="#EF6C00" />
            <StatCard icon={<PurchaseIcon />} label={t.totalPOs} value={report.totalPurchaseOrders} color="#1565C0" />
            <StatCard icon={<InvoiceIcon />} label={t.totalInvoices} value={report.totalInvoices} color="#00796B" />
            <StatCard icon={<BranchIcon />} label={`${t.totalAmount} (${t.kwd})`} value={fmt(report.totalAmount)} color="#C62828" />
          </Box>

          {report.custodies.length === 0 && (
            <Alert severity="info">{t.noData}</Alert>
          )}

          {/* Empty placeholder kept for diff anchor */}
          {report.custodies.map((c) => (
            <Paper key={c.custodyId} sx={{ mb: 3, overflow: 'hidden', border: `2px solid ${isDark ? '#26A69A' : '#00796B'}` }}>
              <Box sx={{ p: 2, background: 'linear-gradient(135deg, #00796B 0%, #00ACC1 100%)', color: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon /> {c.custodyNumber} — {isRTL ? (c.holderNameAr || c.holderName) : c.holderName || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.95 }}>
                      {c.holderEmployeeCode && `${c.holderEmployeeCode} • `}
                      {c.custodyType && `${t.custodyType}: ${c.custodyType} • `}
                      {t.currentBalance}: <strong>{fmt(c.currentBalance)}</strong> {t.kwd} •{' '}
                      {t.initialAmount}: {fmt(c.initialAmount)} {t.kwd}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${c.totalRequests} ${t.totalRequests}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 'bold' }} />
                    <Chip label={`${c.totalPurchaseOrders} ${t.totalPOs}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 'bold' }} />
                    <Chip label={`${c.totalInvoices} ${t.totalInvoices}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 'bold' }} />
                    <Chip label={`${fmt(c.totalAmount)} ${t.kwd}`} size="small" sx={{ bgcolor: '#fff', color: '#00796B', fontWeight: 'bold' }} />
                  </Box>
                </Box>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#00796B', isDark ? 0.2 : 0.08) }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t.branch}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t.requestCount}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t.poCount}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t.invoiceCount}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t.amount}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {c.branches.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ color: theme.palette.text.secondary, py: 3 }}>{t.noData}</TableCell></TableRow>
                  )}
                  {c.branches.map(b => (
                    <TableRow key={b.branchId} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{isRTL ? (b.branchNameAr || b.branchName) : b.branchName}</TableCell>
                      <TableCell align="center" sx={{ color: '#5E35B1', fontWeight: 600 }}>{b.requestCount}</TableCell>
                      <TableCell align="center" sx={{ color: '#1565C0', fontWeight: 600 }}>{b.purchaseOrderCount}</TableCell>
                      <TableCell align="center" sx={{ color: '#00796B', fontWeight: 600 }}>{b.invoiceCount}</TableCell>
                      <TableCell align="right" sx={{ color: '#C62828', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{fmt(b.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                  {c.branches.length > 0 && (
                    <TableRow sx={{ background: 'linear-gradient(135deg, #00796B 0%, #00ACC1 100%)' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>{t.totalRow}</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>{c.totalRequests}</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>{c.totalPurchaseOrders}</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold' }}>{c.totalInvoices}</TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '1rem' }}>{fmt(c.totalAmount)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </>
      )}

      {/* ✅ Dialog إدارة صلاحيات أمناء المخازن (لمن لديه VIEW_ALL فقط) */}
      <Dialog open={accessDialogOpen} onClose={() => setAccessDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #6A1B9A, #AB47BC)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon /> {t.accessDialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>{t.accessHint}</Alert>
          {accessLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Autocomplete
                size="small"
                options={assignableUsers}
                value={selectedUserForAccess}
                onChange={(_, v) => onSelectUserForAccess(v)}
                getOptionLabel={(u) => `${u.username} — ${isRTL ? (u.employeeNameAr || u.fullNameAr || u.fullName || u.employeeName || '—') : (u.employeeName || u.fullName || u.fullNameAr || u.employeeNameAr || '—')}`}
                isOptionEqualToValue={(a, b) => a.userId === b.userId}
                renderInput={(p) => <TextField {...p} label={t.selectUser} fullWidth />}
                sx={{ mb: 2 }}
              />

              {selectedUserForAccess && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    {isRTL ? 'العُهد المركزية المتاحة:' : 'Available Central Custodies:'}
                  </Typography>
                  {centralCustodies.length === 0 ? (
                    <Alert severity="warning">{t.noCentral}</Alert>
                  ) : (
                    <List dense>
                      {centralCustodies.map(c => (
                        <ListItem key={c.custodyId} disablePadding>
                          <ListItemButton onClick={() => toggleAccessCustody(c.custodyId)}>
                            <Checkbox
                              edge="start"
                              checked={selectedAccessCustodyIds.has(c.custodyId)}
                              tabIndex={-1}
                              disableRipple
                            />
                            <ListItemText
                              primary={<Typography sx={{ fontWeight: 'bold', color: '#00796B' }}>{c.custodyNumber}</Typography>}
                              secondary={`${c.employeeCode || ''} — ${isRTL ? (c.employeeNameAr || c.employeeName) : (c.employeeName || c.employeeNameAr) || '—'}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessDialogOpen(false)}>{t.close}</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!selectedUserForAccess || accessSaving}
            onClick={saveAccess}
            sx={{ bgcolor: '#6A1B9A' }}
          >
            {accessSaving ? '...' : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustodyPurchasesReportPage;
